import { useEffect, useState } from "react";
import { Loader2, Calendar as CalendarIcon, CreditCard, AlertCircle, RotateCcw } from "lucide-react";
import api from "@/lib/api";

const KIND_META = {
  cut_off: { label: "Corte", color: "violet", icon: CreditCard },
  payment: { label: "Pago tarjeta", color: "fuchsia", icon: CreditCard },
  debt: { label: "Deuda", color: "rose", icon: AlertCircle },
  recurring: { label: "Recurrente", color: "amber", icon: RotateCcw },
};

export default function CalendarPage() {
  const [data, setData] = useState({ events: [], days: 30 });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    api.get(`/calendar/upcoming?days=${days}`).then(r => setData(r.data || { events: [] })).finally(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-violet-400" /></div>;

  // Group by date
  const byDate = data.events.reduce((acc, ev) => {
    (acc[ev.date] ||= []).push(ev);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto p-5 sm:p-6 space-y-6" data-testid="calendar-page">
      <header className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-violet-300 font-bold">CALENDARIO</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold">Próximos {days} días</h1>
          <p className="text-sm text-slate-400 mt-1">{data.events.length} eventos · tarjetas, deudas y recurrentes</p>
        </div>
        <div className="flex bg-white/[0.04] border border-white/10 rounded-xl p-1">
          {[15, 30, 60, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} data-testid={`calendar-range-${d}`} className={`text-xs font-bold px-3 py-1.5 rounded-lg ${days === d ? "bg-violet-500/30 text-white" : "text-slate-400 hover:text-white"}`}>
              {d}d
            </button>
          ))}
        </div>
      </header>

      {data.events.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-white/15 p-16 text-center">
          <CalendarIcon className="w-12 h-12 text-violet-400 mx-auto mb-3 opacity-60" />
          <h3 className="text-lg font-bold">Sin eventos próximos</h3>
          <p className="mt-1 text-sm text-slate-400">No tienes cortes, pagos ni deudas en los próximos {days} días.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.keys(byDate).sort().map(date => (
            <div key={date} className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden" data-testid={`calendar-day-${date}`}>
              <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
                <div className="font-bold">{formatDate(date)}</div>
                <div className="text-[11px] text-slate-400">{byDate[date].length} evento{byDate[date].length > 1 ? "s" : ""}</div>
              </div>
              <div className="divide-y divide-white/5">
                {byDate[date].map((ev, i) => {
                  const meta = KIND_META[ev.kind] || KIND_META.recurring;
                  const Icon = meta.icon;
                  const accent = meta.color === "rose" ? "text-rose-300 bg-rose-500/15"
                    : meta.color === "amber" ? "text-amber-300 bg-amber-500/15"
                    : meta.color === "fuchsia" ? "text-fuchsia-300 bg-fuchsia-500/15"
                    : "text-violet-300 bg-violet-500/15";
                  return (
                    <div key={i} className="px-5 py-3 flex items-center gap-3 hover:bg-white/[0.02]" data-testid={`calendar-event-${ev.kind}-${i}`}>
                      <div className={`w-9 h-9 rounded-xl ${accent} flex items-center justify-center`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{ev.title}</div>
                        <div className="text-[11px] text-slate-500">{meta.label}</div>
                      </div>
                      {ev.amount != null && (
                        <div className="text-sm font-bold tabular-nums">{ev.currency} ${Number(ev.amount).toFixed(2)}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
}
